import type { NavigateTarget, TurnDone } from "../api/stream";
import { addAttachment, attachmentsFrom, type TurnAttachment } from "../api/turn-attachments";
import type { SessionMessage, TurnStep } from "../api/use-agents";
import type { TurnMeter, TurnTokens } from "./activity";

/**
 * O que fica na conversa depois que um turno acontece.
 *
 * Saiu de `use-turn-stream.ts`, que é um composable e tinha passado do teto de 150
 * linhas: lá ficou o TURNO (o fio, o abortar, o estado do orbe), aqui ficou a
 * FORMA — a fala que a tela desenha e a função que a monta. Nada mudou de
 * comportamento na mudança de casa.
 */
/** A ferramenta, na forma que o cartão de atividade desenha. Mora em
 *  `activity.ts` desde que o cartão passou a ser o dono dela; reexportada daqui
 *  para quem já a pedia a este ficheiro não ter de saber da mudança. */
export type { LiveTool } from "./activity";

export interface Bubble {
	/** O id da MENSAGEM guardada. Só as falas relidas o têm — é ele que torna o
	 *  lixinho possível; uma fala ao vivo ainda não tem endereço para apagar. */
	id?: string;
	/** `agent`: o resultado de um subagente em segundo plano — nem a pessoa, nem o Ben. */
	role: "user" | "assistant" | "agent";
	text: string;
	steps?: TurnStep[];
	silent?: boolean;
	/**
	 * O PROVEDOR RECUSOU esta troca (a pergunta e a resposta vazia). Fica na tela
	 * e fora do histórico que se reenvia: reenviada, cada pedido seguinte da
	 * conversa era recusado de novo.
	 */
	refused?: boolean;
	failed?: boolean;
	/** Miniaturas do que foi junto, para a thread não mentir sobre o que se mandou. */
	images?: string[];
	/** A nota de voz que foi junto — tocável na hora; depois do F5 vem como anexo. */
	audio?: string;
	/**
	 * Os arquivos que esta fala ENTREGA.
	 *
	 * Separado de `images`, que é o que a pessoa mandou PARA o agente e chega como
	 * endereço pronto. Aqui é o caminho contrário: o Ben tem um arquivo do
	 * workspace em mãos e a conversa precisa dar como abri-lo e baixá-lo, em vez de
	 * colar o conteúdo como texto ou oferecer mandar por WhatsApp.
	 */
	attachments?: TurnAttachment[];
	/** Quando chegou e quanto custou — os dois já vinham no fio e eram descartados. */
	at?: number;
	tokens?: number;
	/** Qual turno escreveu isto. É o que torna o polegar possível; ausente quando a
	 *  linha de auditoria não pôde ser escrita, e então não há o que avaliar. */
	turnId?: string | null;
	/** Aonde este turno levou a pessoa: o cartão fica na conversa depois que a rota muda. */
	navigate?: NavigateTarget[];
	/**
	 * O QUE O BEN ESCREVEU ANTES DA RESPOSTA — o raciocínio, guardado.
	 *
	 * Um turno tem várias corridas de texto: o modelo escreve, chama uma
	 * ferramenta, escreve outra vez. Só a ÚLTIMA é a resposta; as anteriores são
	 * pensamento em voz alta. Enquanto o turno corre, o `LiveTurn` mostra-as numa
	 * gaveta — e ao terminar a gaveta ia embora com ele.
	 *
	 * O efeito, dito pelo dono (16/08): "pensou, escreveu… depois o que tava
	 * escrevendo sumiu da resposta principal". Não sumiu por defeito — a resposta
	 * é mesmo só a última corrida —, mas quem viu o texto ser escrito não tem como
	 * saber disso, e a tela deixava de ter onde o mostrar. Agora a bolha guarda-o e
	 * a gaveta sobrevive ao fim do turno.
	 */
	thinking?: string;
	/**
	 * OS TOKENS SEPARADOS, e a conta do turno.
	 *
	 * `tokens` acima continua a ser a soma, que é o que uma barra estreita mostra.
	 * Estes dois são o que o cartão de atividade desenha no rodapé do modo DEV — e
	 * ambos são opcionais porque uma fala RELIDA de uma sessão antiga não os tem: o
	 * `meter` só passou a viajar no fio em 24/08 e não é persistido na mensagem.
	 */
	usage?: TurnTokens;
	meter?: TurnMeter;
	/** A virada parou à espera de uma pessoa. A conversa diz isso em vez de deixar
	 *  a resposta parecer o fim. */
	awaitsHuman?: boolean;
}

/** A bolha que o turno deixa: `done` é o que veio do fio, a bolha é o que a tela
 *  lê. Fora do `onDone` porque montá-la lá dentro estourava a complexidade. */
export function assistantBubble(
	done: TurnDone,
	went: readonly NavigateTarget[],
	/** O que chegou pelo evento `attachment` durante o turno; a lista do `done`
	 *  entra aqui também, e o id decide o que é repetição. */
	handed: readonly TurnAttachment[],
): Bubble {
	const files = attachmentsFrom(done.attachments).reduce(addAttachment, [...handed]);

	return {
		role: "assistant",
		// A resposta COMPOSTA, não os deltas: só a última corrida de texto é entregue,
		// e os deltas incluem as anteriores.
		text: done.silent || done.refused ? "" : done.reply,
		...(done.steps.length ? { steps: done.steps } : {}),
		...(done.refused ? { refused: true } : done.silent ? { silent: true } : {}),
		...(went.length ? { navigate: [...went] } : {}),
		...(files.length ? { attachments: files } : {}),
		/**
		 * O raciocínio e o texto não entregue, JUNTOS — e só aqui.
		 *
		 * Na conversa com o Ben não há porta de entrega: a audiência é quem opera,
		 * o texto do modelo É a conversa, e nada é suprimido. As duas naturezas
		 * ficam separadas onde a diferença morde, que é a caixa de entrada
		 * (`inbox/.../TurnPanel.vue`) — ali uma frase escrita para o cliente e não
		 * enviada precisa de dizer que o foi.
		 */
		...(thinkingOf([...(done.thinking ?? []), ...(done.withheld ?? [])])
			? { thinking: thinkingOf([...(done.thinking ?? []), ...(done.withheld ?? [])]) }
			: {}),
		at: Date.now(),
		// Entrada mais saída num número só: dois convidam a uma conta de cabeça.
		tokens: done.usage.inputTokens + done.usage.outputTokens,
		// E os mesmos números SEPARADOS, para quem os sabe ler. Ver `Bubble.usage`.
		usage: {
			input: done.usage.inputTokens,
			output: done.usage.outputTokens,
			...(done.usage.cachedInputTokens ? { cached: done.usage.cachedInputTokens } : {}),
			...(done.usage.reasoningTokens ? { reasoning: done.usage.reasoningTokens } : {}),
		},
		...(done.meter ? { meter: done.meter } : {}),
		...(done.awaitsHuman ? { awaitsHuman: true } : {}),
		turnId: done.turnId ?? null,
	};
}

/**
 * As corridas de pensamento, numa string legível — ou nada.
 *
 * Linha em branco entre elas porque cada corrida é um momento distinto do turno
 * (antes de uma ferramenta, depois dela), e coladas leem-se como um parágrafo só
 * que se contradiz.
 */
function thinkingOf(runs: readonly string[] | undefined): string {
	return (runs ?? [])
		.map((run) => run.trim())
		.filter(Boolean)
		.join("\n\n");
}

/**
 * Uma fala GUARDADA, relida quando a conversa reabre.
 *
 * Irmã de `assistantBubble`: uma traduz o fio, esta traduz o que o servidor
 * arquivou, e as duas terminam na mesma `Bubble` — senão a conversa reaberta seria
 * uma segunda tela com regras próprias, que é como uma metade fica para trás.
 *
 * As imagens voltam por ENDEREÇO, e não pelo `blob:` do envio, que morre com a aba
 * (migração 0071). Os anexos voltam pelo mesmo leitor do fio, e o `url` que o
 * servidor guardou é descartado junto: uma URL assinada de ontem já expirou, e
 * quem resolve é a `UploadPort`, na hora do clique.
 */
export function rememberedBubble(message: SessionMessage): Bubble {
	const files = attachmentsFrom(message.attachments);
	const tokens = message.usage.inputTokens + message.usage.outputTokens;
	return {
		id: message.id,
		role: message.role,
		text: message.content,
		...(message.steps.length ? { steps: message.steps } : {}),
		...(message.images?.length ? { images: [...message.images] } : {}),
		...(files.length ? { attachments: files } : {}),
		/**
		 * A HORA E A CONTA, que o servidor sempre mandou e esta função deitava fora.
		 *
		 * O sintoma era um F5: a conversa reabria e cada resposta perdia o relógio e
		 * a contagem — e como a barra de ações só existe quando há `at`, perdia
		 * também o copiar, o regerar e o polegar. Metade da conversa deixava de ter
		 * o que a outra metade tinha, por causa de dois campos não copiados.
		 *
		 * O `meter` e o `turnId` voltaram a viajar com a migração 0148: a linha do
		 * modelo e o polegar sobrevivem ao F5 nas falas escritas depois dela. O que
		 * continua a não voltar é só o raciocínio, que segue não persistido.
		 */
		at: new Date(message.createdAt).getTime(),
		...(tokens > 0
			? {
					tokens,
					usage: { input: message.usage.inputTokens, output: message.usage.outputTokens },
				}
			: {}),
		...(message.meter ? { meter: message.meter } : {}),
		...(message.turnId ? { turnId: message.turnId } : {}),
	};
}
